import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageIcon, Mail, MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const FILTERS = [["open", "Open"], ["done", "Done"], ["all", "All"]];

const shotCache = new Map();

const Screenshot = ({ id }) => {
  const [url, setUrl] = useState(shotCache.get(id) || "");
  useEffect(() => {
    if (shotCache.has(id)) return;
    api.ownerScreenshot(id).then((b) => { const u = URL.createObjectURL(b); shotCache.set(id, u); setUrl(u); }).catch(() => {});
  }, [id]);
  if (!url) return <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><ImageIcon className="w-3.5 h-3.5" /> Loading screenshot…</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`owner-request-shot-${id}`}>
      <img src={url} alt="Attached screenshot" className="max-h-48 rounded-lg border border-border hover:opacity-90 transition-opacity" />
    </a>
  );
};

const RequestCard = ({ r, onToggle }) => (
  <div className={`bg-card border rounded-xl p-5 space-y-3 ${r.status === "done" ? "border-border opacity-70" : "border-amber-500/40"}`} data-testid={`owner-request-${r.id}`}>
    <div className="flex items-start gap-3">
      <Checkbox checked={r.status === "done"} onCheckedChange={(v) => onToggle(r, !!v)} className="mt-1 h-5 w-5" data-testid={`owner-request-done-${r.id}`} aria-label="Mark done" />
      <div className="min-w-0 flex-1">
        <p className="font-display font-bold text-lg leading-tight">{r.company_name}</p>
        <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
          <span>{when(r.created_at)}</span>
          {r.depot_name && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.depot_name}</span>}
          <a href={`mailto:${r.from_email}?subject=${encodeURIComponent("Your Cross Jobs change request")}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail className="w-3 h-3" />{r.from_name} · {r.from_email}</a>
        </p>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "done" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"}`} data-testid={`owner-request-status-${r.id}`}>
        {r.status === "done" ? "Done" : "Open"}
      </span>
    </div>
    <p className="text-sm whitespace-pre-wrap pl-8" data-testid={`owner-request-message-${r.id}`}>{r.message}</p>
    {r.has_screenshot && <div className="pl-8"><Screenshot id={r.id} /></div>}
  </div>
);

export const RequestsPanel = ({ requests, setRequests }) => {
  const [filter, setFilter] = useState("open");
  const shown = requests.filter((r) => filter === "all" || r.status === filter);
  const toggle = async (r, done) => {
    try {
      const u = await api.ownerMarkRequest(r.id, done);
      setRequests((xs) => xs.map((x) => (x.id === u.id ? u : x)));
      toast.success(done ? "Marked done" : "Reopened");
    } catch { toast.error("Could not update that request"); }
  };
  return (
    <div className="space-y-5" data-testid="owner-requests">
      <div className="flex gap-1.5">
        {FILTERS.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`owner-requests-filter-${v}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === v ? "bg-primary text-white" : "border border-border hover:bg-secondary"}`}>
            {label} <span className="font-mono text-xs opacity-80">{v === "all" ? requests.length : requests.filter((r) => r.status === v).length}</span>
          </button>
        ))}
      </div>
      {shown.length === 0
        ? <div className="text-center py-16 border border-dashed border-border rounded-2xl text-muted-foreground" data-testid="owner-requests-empty">{filter === "open" ? "No open requests — you're all caught up." : "Nothing here yet."}</div>
        : <div className="grid lg:grid-cols-2 gap-4">{shown.map((r) => <RequestCard key={r.id} r={r} onToggle={toggle} />)}</div>}
    </div>
  );
};
